import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Circle, Briefcase } from 'lucide-react';
import { API_BASE, STAGE_COLORS, STAGE_ICONS, PIPELINE_SLUG_MAP, PIPELINE_ICONS, CLOSED_STAGES } from './pipeline/constants';
import { PipelineMoveDialog } from './pipeline/PipelineMoveDialog';
import { PipelineStageSummary } from './pipeline/PipelineStageSummary';
import { PipelineBoard } from './pipeline/PipelineBoard';
import { PipelineStatsCards } from './pipeline/PipelineStatsCards';
import { PipelineHeader } from './pipeline/PipelineHeader';
export default function DynamicPipeline() {
  const { pipelineSlug } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [deals, setDeals] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [stats, setStats] = useState({ total: 0, value: 0, byStage: {} });
  const [pipelineStages, setPipelineStages] = useState([]);
  const [pipelineInfo, setPipelineInfo] = useState(null);
  const [allPipelines, setAllPipelines] = useState([]);

  const pipelineName = PIPELINE_SLUG_MAP[pipelineSlug] || pipelineSlug;

  useEffect(() => {
    fetchPipelineStages();
    fetchPipelineData();
  }, [pipelineSlug]);

  const fetchPipelineStages = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/bigin/pipelines`, {
        headers: { }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAllPipelines(data.pipelines || []);
        
        // Find the matching pipeline
        const matchingPipeline = data.pipelines?.find(p => 
          p.name === pipelineName || 
          p.name?.toLowerCase().includes(pipelineSlug?.replace(/-/g, ' '))
        );
        
        if (matchingPipeline) {
          setPipelineInfo(matchingPipeline);
          
          // Filter out closed stages for the main board
          const activeStages = matchingPipeline.stages?.filter(s => 
            !['Completed', 'Cancelled', 'Closed Won', 'Closed Lost'].includes(s.name)
          ) || [];
          
          const dynamicStages = activeStages.map((stage, index) => {
            const colorIndex = index % STAGE_COLORS.length;
            const stageName = stage.name || stage;
            return {
              id: stageName,
              label: stageName,
              ...STAGE_COLORS[colorIndex],
              icon: STAGE_ICONS[stageName] || STAGE_ICONS['default']
            };
          });
          setPipelineStages(dynamicStages);
        } else {
          toast.error(`Pipeline "${pipelineName}" not found`);
        }
      }
    } catch (error) {
      toast.error('Failed to load pipeline configuration');
    }
  };

  const fetchPipelineData = async () => {
    try {
      setLoading(true);
      
      // Fetch deals
      const dealsResponse = await fetch(`${API_BASE}/api/bigin/deals?limit=200`, {
        headers: { }
      });
      
      if (dealsResponse.ok) {
        const dealsData = await dealsResponse.json();
        // Filter deals for this pipeline - match by partial name or layout
        const pipelineNameLower = pipelineName?.toLowerCase();
        const pipelineSlugLower = pipelineSlug?.replace(/-/g, ' ').toLowerCase();
        
        const pipelineDeals = (dealsData.deals || []).filter(d => {
          const dealPipeline = (d.Pipeline || '').toLowerCase();
          const layoutName = (d.layout_info?.name || d.Layout?.name || '').toLowerCase();
          
          return (
            dealPipeline.includes(pipelineNameLower) ||
            dealPipeline.includes(pipelineSlugLower) ||
            layoutName.includes(pipelineNameLower) ||
            layoutName.includes(pipelineSlugLower) ||
            pipelineNameLower?.includes(dealPipeline.replace(' standard', '')) ||
            pipelineSlugLower?.includes(dealPipeline.replace(' standard', ''))
          );
        });
        setDeals(pipelineDeals);
        
        // Calculate stats
        const totalValue = pipelineDeals.reduce((sum, d) => sum + (d.Amount || 0), 0);
        const byStage = {};
        pipelineDeals.forEach(d => {
          const stage = d.Stage || 'Unknown';
          byStage[stage] = (byStage[stage] || 0) + 1;
        });
        
        setStats({
          total: pipelineDeals.length,
          value: totalValue,
          byStage
        });
      }
      
      // Get last sync time
      const syncResponse = await fetch(`${API_BASE}/api/bigin/status`, {
        headers: { }
      });
      if (syncResponse.ok) {
        const syncData = await syncResponse.json();
        setLastSync(syncData.last_sync);
      }
      
    } catch (error) {
      toast.error('Failed to load pipeline data');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_BASE}/api/bigin/sync`, {
        method: 'POST',
        headers: { }
      });
      
      if (response.ok) {
        toast.success('Data synced from Zoho Bigin');
        await fetchPipelineStages();
        await fetchPipelineData();
      } else {
        toast.error('Sync failed');
      }
    } catch (error) {
      toast.error('Failed to sync data');
    } finally {
      setSyncing(false);
    }
  };

  const handleMoveToStage = async (deal, newStage) => {
    const dealId = deal.bigin_id || deal.id;
    setUpdating(dealId);
    
    try {
      const response = await fetch(`${API_BASE}/api/bigin/deals/${dealId}/stage?stage=${encodeURIComponent(newStage)}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || `Deal moved to ${newStage}`);
        setMoveDialogOpen(false);
        await fetchPipelineData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to move deal');
      }
    } catch (error) {
      toast.error('Failed to update deal stage');
    } finally {
      setUpdating(null);
    }
  };

  const handleMoveNext = async (deal) => {
    const currentStageIndex = pipelineStages.findIndex(s => s.id === deal.Stage);
    if (currentStageIndex === -1 || currentStageIndex >= pipelineStages.length - 1) {
      toast.info('Deal is already at the last stage');
      return;
    }
    
    const nextStage = pipelineStages[currentStageIndex + 1];
    await handleMoveToStage(deal, nextStage.id);
  };

  const getDealsForStage = (stageId) => {
    return deals.filter(d => d.Stage === stageId);
  };

  const getStageConfig = (stageName) => {
    return [...pipelineStages, ...CLOSED_STAGES].find(s => s.id === stageName) || 
      { color: 'bg-gray-500', lightColor: 'bg-gray-50 border-gray-200', textColor: 'text-gray-700', icon: Circle };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const PipelineIcon = PIPELINE_ICONS[pipelineName] || Briefcase;

  if (loading && pipelineStages.length === 0) {
    return (
      <div className="p-3 sm:p-4 md:p-6 space-y-4 md:space-y-6" data-testid="dynamic-pipeline">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <Skeleton className="h-8 w-48 sm:w-64" />
          <Skeleton className="h-10 w-24 sm:w-32" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-24 sm:h-32 w-full" />
          ))}
        </div>
        <div className="flex gap-3 overflow-x-auto pb-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-64 sm:h-96 w-60 sm:w-72 flex-shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 space-y-4 md:space-y-6" data-testid="dynamic-pipeline">
      {/* Header */}
      <PipelineHeader PipelineIcon={PipelineIcon} formatCurrency={formatCurrency} formatDate={formatDate} handleSync={handleSync} lastSync={lastSync} pipelineName={pipelineName} stats={stats} syncing={syncing} />

      {/* Stats Cards */}
      <PipelineStatsCards formatCurrency={formatCurrency} pipelineStages={pipelineStages} stats={stats} />

      {/* Pipeline Board */}
      <PipelineBoard deals={deals} formatCurrency={formatCurrency} formatDate={formatDate} getDealsForStage={getDealsForStage} handleMoveNext={handleMoveNext} loading={loading} pipelineInfo={pipelineInfo} pipelineStages={pipelineStages} setMoveDialogOpen={setMoveDialogOpen} setSelectedDeal={setSelectedDeal} updating={updating} />

      {/* Stage Summary Table */}
      <PipelineStageSummary formatCurrency={formatCurrency} getDealsForStage={getDealsForStage} pipelineStages={pipelineStages} stats={stats} />

      {/* Move to Stage Dialog - Responsive */}
      <PipelineMoveDialog handleMoveToStage={handleMoveToStage} moveDialogOpen={moveDialogOpen} pipelineInfo={pipelineInfo} pipelineStages={pipelineStages} selectedDeal={selectedDeal} setMoveDialogOpen={setMoveDialogOpen} updating={updating} />
    </div>
  );
}
